import OwnerEditOrder from "./OwnerEditOrder";
import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { dms, inr, niceDate, statusPill } from "./api";
import { PageHeader } from "./OwnerPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, ChevronRight, Store, Truck, Receipt, IndianRupee, MapPin, Printer, FileText, Trash2, Edit } from "lucide-react";
import LocationDocumentsBlock from "./LocationDocumentsBlock";
import { DocumentsGallery } from "./OwnerPages";
import LinkInvoiceButton from "./LinkInvoiceButton";
import ProofButton from "./ProofButton";
import { LedgerEntryDialog } from "./ReturnDetail";

// Distributor: retailers list
export function DistRetailersPage() {
  const [list, setList] = useState([]);
  const [sps, setSps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [ledgerFor, setLedgerFor] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const nav = useNavigate();
  const load = () => dms.listRetailers({ lite: 1 }).then(d => setList(d.data));
  useEffect(() => { load(); dms.distSalespersons("me").then(d => setSps(d.data || [])).catch(() => setSps([])); }, []);
  // Point 72: search by retailer name or phone
  const filteredList = list.filter((r) => {
    if (typeFilter && (r.retailer_type || "retailer") !== typeFilter) return false;
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (r.name || "").toLowerCase().includes(needle)
      || (r.phone || "").toLowerCase().includes(needle);
  });

  const openLedger = async (r) => {
    setLedgerFor(r);
    setLedgerLoading(true);
    try {
      const res = await dms.secondaryLedger(r.id);
      const s = (res.summary || []).find(x => x.retailer_id === r.id) || { billed: r.total_purchases || 0, paid: 0, outstanding: r.outstanding || 0 };
      setLedgerData({ summary: s, entries: res.entries || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ledger");
      setLedgerData(null);
    }
    setLedgerLoading(false);
  };
  const openNew = () => { setForm({ name: "", phone: "", address: "", region: "", email: "", password: "Demo@2026", gstin: "", shop_license: "", salesperson_id: "", credit_limit: 100000, retailer_type: "retailer" }); setOpen(true); };
  const save = async () => {
    if (!form.salesperson_id) { toast.error("Select a Salesperson"); return; }
    // If a login (email) is being created, require full details + KYC + document.
    if (String(form.email || "").trim()) {
      const req = { region: "Region", gstin: "GSTIN", shop_license: "Shop License", password: "Password" };
      const missing = Object.entries(req)
        .filter(([k]) => !String(form[k] || "").trim())
        .map(([, label]) => label);
      if (!(form.documents || []).length) missing.push("at least one Document");
      if (missing.length) {
        toast.error("To create a retailer login, complete: " + missing.join(", "));
        return;
      }
    }
    try { await dms.createRetailer(form); toast.success("Retailer added"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <PageHeader title="My Retailers" subtitle="Manage retailers under your distribution"
        action={<Button onClick={openNew} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="add-retailer-btn"><Plus size={16} className="mr-1" /> Add Retailer</Button>} />
      <div className="mb-3 flex gap-2">
        <Input className="flex-1" placeholder="Search retailer by name or phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dist-retailers-search-input" />
        <select className="border rounded-md h-9 px-2 text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="ret-type-filter">
            <option value="">All types</option>
            <option value="retailer">Retailer</option>
            <option value="wholeseller">Wholeseller</option>
            <option value="workshop">Workshop</option>
            <option value="mechanic">Mechanic</option>
          </select>
      </div>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Retailer</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Location</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
            <TableHead className="text-right">Total Purchases</TableHead>
            <TableHead>Last Order</TableHead>
            <TableHead className="text-right">Manage</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filteredList.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">{(q || typeFilter) ? "No matching retailers" : "No retailers yet"}</TableCell></TableRow>}
            {filteredList.map(r => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openLedger(r)} data-testid={`dist-ret-row-${r.id}`}>
                <TableCell>
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.phone}</div>
                </TableCell>
                <TableCell className="text-xs">{((r.retailer_type || "retailer").charAt(0).toUpperCase() + (r.retailer_type || "retailer").slice(1))}</TableCell>
                <TableCell>
                  <div className="text-xs text-slate-600 truncate max-w-[240px]">{r.address}</div>
                  {r.gps_lat && <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1"><MapPin size={10} /> {r.gps_lat.toFixed(4)}, {r.gps_lng.toFixed(4)}</div>}
                </TableCell>
                <TableCell className={`text-right font-medium ${(r.outstanding || 0) > 0 ? "text-rose-700" : "text-slate-500"}`}>{inr(r.outstanding || 0)}</TableCell>
                <TableCell className="text-right">{inr(r.total_purchases || 0)}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.last_order_at ? niceDate(r.last_order_at) : "—"}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => nav(`/dms/distributor/retailers/${r.id}`)} className="text-[#a67c00] text-xs font-medium hover:underline" data-testid={`manage-ret-${r.id}`}>
                    Manage <ChevronRight size={12} className="inline" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!ledgerFor} onOpenChange={o => { if (!o) { setLedgerFor(null); setLedgerData(null); } }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ledger — {ledgerFor?.name}</DialogTitle></DialogHeader>
          {ledgerLoading && <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>}
          {!ledgerLoading && ledgerData && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <Card className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Billed</div>
                  <div className="text-lg font-bold text-slate-900 mt-0.5">{inr(ledgerData.summary.billed || 0)}</div>
                </Card>
                <Card className="p-3 bg-emerald-50 border-emerald-200">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700">Paid</div>
                  <div className="text-lg font-bold text-emerald-900 mt-0.5">{inr(ledgerData.summary.paid || 0)}</div>
                </Card>
                <Card className="p-3 bg-rose-50 border-rose-200">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700">Outstanding</div>
                  <div className="text-lg font-bold text-rose-900 mt-0.5">{inr(ledgerData.summary.outstanding || 0)}</div>
                </Card>
              </div>
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {ledgerData.entries.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No transactions</TableCell></TableRow>}
                    {ledgerData.entries.map((e, i) => (
                      <TableRow key={e.id || i}>
                        <TableCell className="text-xs">{niceDate(e.at)}</TableCell>
                        <TableCell><span className="text-xs bg-slate-100 px-2 py-0.5 rounded capitalize">{(e.kind || "").replace(/_/g, " ")}</span></TableCell>
                        <TableCell className="font-mono text-xs">{e.reference_no || e.reference_id || "—"}</TableCell>
                        <TableCell className="text-right font-medium">{inr(e.amount || 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLedgerFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader><DialogTitle>Add Retailer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Shop Name *</Label><Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="ret-name" /></div>
            <div><Label>Phone *</Label><Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address *</Label><Textarea rows={2} value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Region</Label><Input value={form.region || ""} onChange={e => setForm({ ...form, region: e.target.value })} /></div>
            <div><Label>Credit Limit (₹)</Label><Input type="number" value={form.credit_limit || ""} onChange={e => setForm({ ...form, credit_limit: Number(e.target.value) })} /></div>
            <div className="col-span-2"><Label>Retailer Type *</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.retailer_type || "retailer"} onChange={e => setForm({ ...form, retailer_type: e.target.value })} data-testid="ret-type">
                <option value="retailer">Retailer</option>
                <option value="wholeseller">Wholeseller</option>
                <option value="workshop">Workshop</option>
                <option value="mechanic">Mechanic</option>
              </select></div>
            <div className="col-span-2">
              <Label>Salesperson *</Label>
              <Select value={form.salesperson_id || ""} onValueChange={v => setForm(f => ({ ...f, salesperson_id: v }))}>
                <SelectTrigger data-testid="ret-sp"><SelectValue placeholder={sps.length ? "Select salesperson" : "No salespersons assigned to you yet"} /></SelectTrigger>
                <SelectContent>{sps.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Login Email (optional)</Label><Input type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="ret-email" /></div>
            <div><Label>Password</Label><Input value={form.password || ""} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin || ""} onChange={e => setForm({ ...form, gstin: e.target.value })} /></div>
            <div><Label>Shop License</Label><Input value={form.shop_license || ""} onChange={e => setForm({ ...form, shop_license: e.target.value })} /></div>
            <div className="col-span-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
              If you set a <b>Login Email</b>, the retailer login is created only after Region, GSTIN, Shop License, Password &amp; at least one document are provided.
            </div>
            <div className="col-span-2 text-xs uppercase tracking-wider font-semibold text-slate-500 pt-1">Bank & UPI (optional)</div>
            <div><Label>Bank Name</Label><Input value={form.bank_name || ""} onChange={e => setForm({ ...form, bank_name: e.target.value })} data-testid="ret-bank-name" /></div>
            <div><Label>Account No.</Label><Input value={form.bank_account || ""} onChange={e => setForm({ ...form, bank_account: e.target.value })} /></div>
            <div><Label>IFSC</Label><Input value={form.bank_ifsc || ""} onChange={e => setForm({ ...form, bank_ifsc: e.target.value })} /></div>
            <div><Label>UPI ID</Label><Input value={form.upi_id || ""} onChange={e => setForm({ ...form, upi_id: e.target.value })} placeholder="name@bank" data-testid="ret-upi" /></div>
            <div className="col-span-2">
              <Label>Payment QR (image)</Label>
              <div className="flex items-center gap-2 mt-1">
                {form.qr_url ? <img src={form.qr_url} alt="qr" className="h-10 w-10 object-contain border rounded" /> : null}
                <input type="file" accept="image/*" data-testid="ret-qr" onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const rd = new FileReader(); rd.onload = () => setForm(v => ({ ...v, qr_url: rd.result })); rd.readAsDataURL(f);
                }} className="text-xs" />
              </div>
            </div>
            <div className="col-span-2">
              <LocationDocumentsBlock
                lat={form.gps_lat ?? ""}
                lng={form.gps_lng ?? ""}
                locationLink={form.location_link || ""}
                onLat={(v) => setForm(f => ({ ...f, gps_lat: v === "" ? null : Number(v) }))}
                onLng={(v) => setForm(f => ({ ...f, gps_lng: v === "" ? null : Number(v) }))}
                onLocationLink={(v) => setForm(f => ({ ...f, location_link: v }))}
                documents={form.documents || []}
                onDocuments={(docs) => setForm(f => ({ ...f, documents: docs }))}
                helpText="After the retailer logs in, they can update their exact Latitude / Longitude if needed."
              />
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-retailer-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DistRetailerDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [tab, setTab] = useState("visibility");
  const [vis, setVis] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const load = async () => {
    const [rr, vv] = await Promise.all([dms.getRetailer(id), dms.getRetVisibility(id)]);
    setR(rr); setVis(vv.data);
  };
  useEffect(() => { load(); }, [id]);
  const toggleVis = async (pid, v) => { await dms.setRetVisibility(id, { product_id: pid, visible: v }); setVis(vis.map(x => x.product_id === pid ? { ...x, visible: v } : x)); };
  const del = async () => {
    if (!window.confirm(`Delete retailer "${r.name}"? This also removes their login.`)) return;
    try { await dms.deleteRetailer(id); toast.success("Retailer deleted"); nav("/dms/distributor/retailers"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  if (!r) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  return (
    <div>
      <PageHeader title={r.name} subtitle={`${r.phone} • ${r.address}`} back="/dms/distributor/retailers"
        />
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        {[{ k: "visibility", l: "Product Visibility" }, { k: "info", l: "Details" }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? "border-[#a67c00] text-[#a67c00]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t.l}</button>
        ))}
      </div>
      {tab === "visibility" && (
        <Card>
          <div className="p-4 border-b border-slate-100 text-sm text-slate-600">Turn OFF products this retailer should not see</div>
          <Table><TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Visible</TableHead></TableRow></TableHeader><TableBody>
            {vis.map(v => (
              <TableRow key={v.product_id}><TableCell className="font-mono text-xs">{v.sku_code}</TableCell><TableCell>{v.product_name}</TableCell><TableCell className="text-right"><Switch checked={v.visible} onCheckedChange={c => toggleVis(v.product_id, c)} /></TableCell></TableRow>
            ))}
          </TableBody></Table>
        </Card>
      )}
      {tab === "info" && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Retailer Details</h3>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="dist-edit-retailer-btn"><Edit size={14} className="mr-1" /> Edit</Button>
          </div>
          <RetailerProfileDialog r={r} open={editOpen} onClose={() => setEditOpen(false)} onSaved={load} />
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {[["Name", r.name], ["Phone", r.phone], ["Address", r.address], ["Region", r.region], ["GSTIN", r.kyc?.gstin], ["Shop License", r.kyc?.shop_license], ["Credit Limit", inr(r.credit_limit)], ["GPS", r.gps_lat ? `${r.gps_lat.toFixed(4)}, ${r.gps_lng.toFixed(4)}` : "—"], ["Bank", r.bank?.bank_name], ["Account No.", r.bank?.bank_account], ["IFSC", r.bank?.bank_ifsc], ["UPI ID", r.bank?.upi_id]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">{k}</span><span className="text-slate-900 font-medium">{v || "—"}</span></div>
            ))}
          </div>
          {r.bank?.qr_url && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 font-semibold">Payment QR</div>
              <img src={r.bank.qr_url} alt="Payment QR" className="h-32 w-32 object-contain border rounded" data-testid="ret-qr-view" />
            </div>
          )}
          <div className="mt-4">
            <DocumentsGallery documents={r.documents || []} locationLink={r.location_link} lat={r.gps_lat} lng={r.gps_lng} />
          </div>
        </Card>
      )}
    </div>
  );
}

// Distributor: secondary orders (received from retailers) + dispatch
export function DistSecondaryOrdersPage() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState(() => new URLSearchParams(window.location.search).get("status") || "");
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const sp = new URLSearchParams(loc.search).get("status") || "";
    setStatus(sp);
  }, [loc.search]);

  const { impersonation } = useAuth();
  const canTrash = !!impersonation; // Owner logged in as a distributor ("Login as")
  const [showTrash, setShowTrash] = useState(false);
  const [retailerF, setRetailerF] = useState("");
  const [trash, setTrash] = useState([]);
  const [trashDays, setTrashDays] = useState(30);

  const loadOrders = () => dms.listSecondaryOrders(status && status !== "balance" ? status : undefined).then(d => setList(d.data)).catch(() => {});
  const loadTrash = () => dms.listDeletedSecondaryOrders().then(d => { setTrash(d.data || []); setTrashDays(d.retention_days || 30); }).catch(() => {});

  useEffect(() => { loadOrders(); }, [status]);
  useEffect(() => { if (canTrash) loadTrash(); }, [showTrash, canTrash]);

  const doSoftDelete = async (e, o) => {
    e.stopPropagation();
    if (!window.confirm(`Delete cancelled order ${o.order_no}?\n\nIt will move to "Deleted Orders" and can be restored within 30 days. After that it is permanently deleted automatically.`)) return;
    try { await dms.softDeleteSecondaryOrder(o.id); toast.success("Order moved to Deleted Orders"); loadOrders(); loadTrash(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };
  const doRestore = async (o) => {
    try { await dms.restoreSecondaryOrder(o.id); toast.success("Order restored"); loadTrash(); loadOrders(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to restore"); }
  };

  const retailerOptions = Array.from(new Map(list.map(o => [o.retailer_id, o.retailer_name])).entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  const shown = list.filter(o =>
    (status !== "balance" || o.is_balance_order) &&
    (status !== "pending" || !o.is_balance_order) &&
    (!retailerF || o.retailer_id === retailerF));

  const changeStatus = (v) => {
    const val = v === "__all__" ? "" : v;
    setStatus(val);
    const params = new URLSearchParams(loc.search);
    if (val) params.set("status", val); else params.delete("status");
    nav({ pathname: loc.pathname, search: params.toString() }, { replace: true });
  };

  return (
    <div>
      <PageHeader title="Retailer Orders" subtitle="Orders retailers placed with you — dispatch to complete" />
      {canTrash && <div className="flex gap-2 mb-3">
        <button onClick={() => setShowTrash(false)} className={`text-sm px-3 py-1.5 rounded-md border ${!showTrash ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>Orders</button>
        <button onClick={() => setShowTrash(true)} className={`text-sm px-3 py-1.5 rounded-md border ${showTrash ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>Deleted Orders{trash.length ? ` (${trash.length})` : ""}</button>
      </div>}
      {canTrash && showTrash && (
        <Card>
          <div className="p-3 text-xs text-slate-500 border-b">Deleted orders can be restored for {trashDays} days. After that they are permanently deleted automatically.</div>
          <Table><TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Retailer</TableHead><TableHead>Total</TableHead><TableHead>Deleted On</TableHead><TableHead>Days Left</TableHead><TableHead></TableHead></TableRow></TableHeader><TableBody>
            {trash.map(o => (
              <TableRow key={o.id}>
                <TableCell className="font-mono text-sm">{o.order_no}</TableCell>
                <TableCell className="font-medium">{o.retailer_name}</TableCell>
                <TableCell className="font-semibold">{inr(o.total)}</TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(o.deleted_at)}</TableCell>
                <TableCell className="text-xs">{o.days_left ?? "—"}</TableCell>
                <TableCell><button onClick={() => doRestore(o)} className="text-xs px-3 py-1 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Restore</button></TableCell>
              </TableRow>
            ))}
          </TableBody></Table>
          {trash.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No deleted orders</div>}
        </Card>
      )}
      <div className={canTrash && showTrash ? "hidden" : ""}>
      <Card className="p-3 mb-3 flex flex-wrap gap-3 max-w-2xl">
        <Select value={retailerF || "__all__"} onValueChange={(v) => setRetailerF(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="dist-orders-retailer-filter"><SelectValue placeholder="All retailers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All retailers</SelectItem>
            {retailerOptions.map(([rid, rname]) => <SelectItem key={rid} value={rid}>{rname}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status || "__all__"} onValueChange={changeStatus}>
          <SelectTrigger className="w-52" data-testid="dist-orders-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="balance">Balance Orders</SelectItem>
            <SelectItem value="invoiced">Invoiced</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </Card>
      <Card>
        <Table><TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Invoice No.</TableHead><TableHead>Retailer</TableHead><TableHead>Ordered By</TableHead><TableHead>Mode</TableHead><TableHead>Total</TableHead><TableHead>Fulfillment</TableHead><TableHead>Status</TableHead><TableHead>Placed</TableHead><TableHead></TableHead></TableRow></TableHeader><TableBody>
          {shown.map(o => (
            <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/dms/distributor/retail-orders/${o.id}`)}>
              <TableCell className="font-mono text-sm">
                {o.order_no}
                {o.is_balance_order && <div className="mt-1"><span className="text-[10px] font-sans px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-300">Balance of {o.balance_of_no}{o.balance_of_invoice_no ? ` (${o.balance_of_invoice_no})` : ""}</span></div>}
                {o.balance_order_no && <div className="mt-1"><span className="text-[10px] font-sans px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-300">Partial → balance in {o.balance_order_no}</span></div>}
              </TableCell>
              <TableCell className="font-mono text-xs">{o.invoice_no ? <span className="font-semibold text-slate-800">{o.invoice_no}</span> : <span className="text-slate-400">NA</span>}</TableCell>
              <TableCell className="font-medium">{o.retailer_name}</TableCell>
              <TableCell>
                {o.placed_by_role === "salesperson" ? (
                  <div className="text-xs">
                    <div className="font-medium text-slate-800">{o.placed_by_name || "Salesperson"}</div>
                    <div className="text-slate-400">for {o.retailer_name}</div>
                  </div>
                ) : (
                  <span className="text-xs text-slate-600">{o.retailer_name} (self)</span>
                )}
              </TableCell>
              <TableCell><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{o.mode === "box_pcs" ? "Box+PCS" : "Box"}</span></TableCell>
              <TableCell className="font-semibold">{inr(o.total)}</TableCell>
              <TableCell><div className="flex items-center gap-2 min-w-[100px]"><div className="w-16 h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-[#faf6e6]0" style={{ width: `${o.fulfillment_pct || 0}%` }} /></div><span className="text-xs">{o.fulfillment_pct || 0}%</span></div></TableCell>
              <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${(o.is_balance_order && o.status === "pending") ? "bg-orange-100 text-orange-700 border-orange-300" : statusPill(o.status)}`}>{(o.is_balance_order && o.status === "pending") ? "balance due" : o.status.replace(/_/g, " ")}</span></TableCell>
              <TableCell className="text-xs text-slate-500">{niceDate(o.created_at)}</TableCell>
              <TableCell>
                {canTrash && o.status === "cancelled" && (
                  <button onClick={(e) => doSoftDelete(e, o)} className="text-xs px-3 py-1 rounded-md border border-red-300 text-red-600 hover:bg-red-50">Delete</button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        {shown.length > 0 && (() => {
          const rows = status === "cancelled" ? shown : shown.filter(x => x.status !== "cancelled");
          let amt = 0, ordP = 0, soldP = 0;
          rows.forEach(x => {
            amt += Number(x.total || 0);
            (x.items || []).forEach(it => {
              const ppb = Number(it.box_qty || it.piece_per_box || 1);
              ordP += Number(it.qty_boxes_ordered || 0) * ppb + Number(it.qty_pcs_ordered || 0);
              const db = Math.max(Number(it.qty_boxes_dispatched || 0), Number(it.qty_boxes_invoiced || 0));
              const dp = Math.max(Number(it.qty_pcs_dispatched || 0), Number(it.qty_pcs_invoiced || 0));
              soldP += db * ppb + dp;
            });
          });
          return (
            <tfoot data-testid="dist-orders-total-row">
              <TableRow className="bg-slate-50 font-semibold">
                <TableCell colSpan={5}>Total ({rows.length} orders)</TableCell>
                <TableCell className="font-bold">{inr(amt)}</TableCell>
                <TableCell colSpan={4} className="text-sm">
                  Ordered: {Math.round(ordP)} pcs &nbsp;|&nbsp; Sold (billed/dispatched): {Math.round(soldP)} pcs
                </TableCell>
              </TableRow>
            </tfoot>
          );
        })()}
        </Table>
        {shown.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No orders</div>}
      </Card>
      </div>
    </div>
  );
}

export function DistSecondaryOrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [dispatch, setDispatch] = useState({}); // product_id -> {boxes, pcs}
  const [busy, setBusy] = useState(false);
  const [ewayInput, setEwayInput] = useState("");
  const [savingEway, setSavingEway] = useState(false);
  const [editNoOpen, setEditNoOpen] = useState(false);
  const [noPrefix, setNoPrefix] = useState("");
  const [noNumber, setNoNumber] = useState("");
  const [noKnownPrefixes, setNoKnownPrefixes] = useState([]);
  const [noAddingPrefix, setNoAddingPrefix] = useState(false);
  const [noNewPrefix, setNoNewPrefix] = useState("");
  const [savingNo, setSavingNo] = useState(false);
  const [editChallanOpen, setEditChallanOpen] = useState(false);
  const [cPrefix, setCPrefix] = useState("");
  const [cNumber, setCNumber] = useState("");
  const [cKnownPrefixes, setCKnownPrefixes] = useState([]);
  const [cAddingPrefix, setCAddingPrefix] = useState(false);
  const [cNewPrefix, setCNewPrefix] = useState("");
  const [savingC, setSavingC] = useState(false);
  const [customInvoiceNo, setCustomInvoiceNo] = useState("");
  const [nextInvoicePreview, setNextInvoicePreview] = useState("");
  const [invoicePrefix, setInvoicePrefix] = useState("");

  // Credit/Cash + payment tracking (mirrors Direct Sales form)
  const [billType, setBillType] = useState("credit");
  const [receivedChecked, setReceivedChecked] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [paymentType, setPaymentType] = useState("cash");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [referenceNo, setReferenceNo] = useState("");
  const [stateOfSupply, setStateOfSupply] = useState("");
  const [description, setDescription] = useState("");
  const [invNotes, setInvNotes] = useState("");
  const [billingName, setBillingName] = useState("");
  const [transportMode, setTransportMode] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const [transporter, setTransporter] = useState("");
  const [lrNo, setLrNo] = useState("");
  const [invAttachment, setInvAttachment] = useState(null);
  const [removedIds, setRemovedIds] = useState(() => new Set());
  const [extraItems, setExtraItems] = useState([]); // [{product_id, product_name, sku_code, box_price, pcs_price, boxes, pcs}]
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemProducts, setAddItemProducts] = useState([]);
  const [addItemPid, setAddItemPid] = useState("");
  const [addItemSearch, setAddItemSearch] = useState("");
  const [addItemBoxes, setAddItemBoxes] = useState("1");
  const [addItemPcs, setAddItemPcs] = useState("0");
  const [lotsByPid, setLotsByPid] = useState({}); // product_id -> [{batch_no, label, qty_boxes}]
  const [manualBatch, setManualBatch] = useState({}); // product_id -> {batch_no: pcs string}; only when "Choose batch" is on

  useEffect(() => {
    if (!order || order.status !== "pending") return;
    let off = false;
    (async () => {
      const out = {};
      await Promise.all(order.items.map(async it => {
        try {
          const r = await dms.distributorStockLots(it.product_id, order.distributor_id);
          out[it.product_id] = r.data || [];
        } catch (e) { out[it.product_id] = []; }
      }));
      if (!off) setLotsByPid(out);
    })();
    return () => { off = true; };
  }, [order?.id, order?.status]);

  const pcsTotal = (it) => {
    const d = dispatch[it.product_id] || { boxes: 0, pcs: 0 };
    const p = order && order.mode === "box_pcs" ? Number(d.pcs || 0) : 0;
    return Math.round(Number(d.boxes || 0) * (it.box_qty || 1)) + p;
  };
  const fifoPreview = (it) => {
    const lots = lotsByPid[it.product_id] || [];
    let need = pcsTotal(it);
    const out = [];
    for (const l of lots) {
      const avail = Math.floor(Number(l.qty_boxes || 0) * (it.box_qty || 1) + 1e-6);
      if (avail <= 0 || need <= 0) continue;
      const take = Math.min(avail, need);
      out.push({ batch_no: l.batch_no, qty_pcs: take });
      need -= take;
    }
    if (need > 0 && lots.length) {
      const last = lots[lots.length - 1].batch_no;
      if (out.length && out[out.length - 1].batch_no === last) out[out.length - 1].qty_pcs += need;
      else out.push({ batch_no: last, qty_pcs: need });
    }
    return out;
  };
  const manualAllocs = (it) => {
    const m = manualBatch[it.product_id];
    if (!m) return null;
    return Object.entries(m).map(([bn, q]) => ({ batch_no: Number(bn), qty_pcs: Number(q || 0) })).filter(x => x.qty_pcs > 0);
  };
  const toggleManual = (it) => {
    setManualBatch(prev => {
      const n = { ...prev };
      if (n[it.product_id]) { delete n[it.product_id]; return n; }
      const init = {};
      fifoPreview(it).forEach(x => { init[x.batch_no] = String(x.qty_pcs); });
      n[it.product_id] = init;
      return n;
    });
  };
  const batchPicker = (it) => {
    const lots = lotsByPid[it.product_id] || [];
    if (lots.length < 2) return null;
    const manual = manualBatch[it.product_id];
    const total = pcsTotal(it);
    const bq = it.box_qty || 1;
    const sum = manual ? Object.values(manual).reduce((s, q) => s + Number(q || 0), 0) : 0;
    return (
      <div className="mt-2 text-xs" data-testid={`batch-picker-${it.product_id}`}>
        {!manual ? (
          <div className="flex items-center gap-2 flex-wrap text-slate-600">
            <span>Batch (FIFO):</span>
            {fifoPreview(it).map(x => <span key={x.batch_no} className="px-1.5 py-0.5 rounded bg-slate-100">B{x.batch_no} · {x.qty_pcs} pcs</span>)}
            <button type="button" onClick={() => toggleManual(it)} className="text-[#a67c00] underline">Choose batch</button>
          </div>
        ) : (
          <div className="space-y-1">
            {lots.map(l => (
              <div key={l.batch_no} className="flex items-center gap-2">
                <span className="w-44 text-slate-600">{l.label || `Batch ${l.batch_no}`} <span className="text-slate-400">({Math.floor(Number(l.qty_boxes || 0) * bq + 1e-6)} pcs in stock)</span></span>
                <Input type="number" min={0} value={manual[l.batch_no] ?? ""} onChange={e => setManualBatch(prev => ({ ...prev, [it.product_id]: { ...prev[it.product_id], [l.batch_no]: e.target.value } }))} className="w-20 h-8" />
                <span>pcs</span>
              </div>
            ))}
            <div className={sum === total ? "text-emerald-600" : "text-rose-600"}>Split {sum} / {total} pcs</div>
            <button type="button" onClick={() => toggleManual(it)} className="text-slate-500 underline">Back to FIFO</button>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    dms.listBankAccounts().then(d => setBankAccounts(d.data || d || [])).catch(() => setBankAccounts([]));
  }, []);

  const openAddItem = async () => {
    setAddItemPid(""); setAddItemBoxes("1"); setAddItemPcs("0"); setAddItemSearch("");
    try {
      const b = await dms.retailerBrowse(order.retailer_id);
      const existingIds = new Set([...order.items.map(it => it.product_id), ...extraItems.map(it => it.product_id)]);
      setAddItemProducts((b.data || b.products || []).filter(p => !existingIds.has(p.id || p.product_id)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load products"); setAddItemProducts([]); }
    setAddItemOpen(true);
  };

  const confirmAddItem = () => {
    const p = addItemProducts.find(x => (x.id || x.product_id) === addItemPid);
    if (!p) { toast.error("Select a product"); return; }
    const boxes = Number(addItemBoxes || 0), pcs = Number(addItemPcs || 0);
    if (boxes <= 0 && pcs <= 0) { toast.error("Enter a quantity"); return; }
    setExtraItems(prev => [...prev, {
      product_id: p.id || p.product_id,
      product_name: p.name || p.product_name,
      sku_code: p.sku_code,
      box_price: p.box_price ?? p.selling_price ?? 0,
      pcs_price: p.pcs_price ?? 0,
      boxes, pcs,
    }]);
    setAddItemOpen(false);
  };

  const removeExtraItem = (pid) => setExtraItems(prev => prev.filter(it => it.product_id !== pid));

  const onPickInvDocument = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setInvAttachment({ name: f.name, url: rd.result });
    rd.readAsDataURL(f);
  };
  const load = () => dms.getSecondaryOrder(id).then(o => {
    setOrder(o);
    setEwayInput(o.bill?.eway_bill_no || "");
    const init = {};
    o.items.forEach(it => init[it.product_id] = { boxes: it.qty_boxes_ordered, pcs: it.qty_pcs_ordered });
    setDispatch(init);
    if (!o.invoice_no) {
      dms.getNumberSettings(o.distributor_id).then(s => {
        const rb = s.retailer_bill || {};
        const prefix = (rb.prefix || "").trim();
        const w = Number(rb.width || 4);
        const seq = String(rb.next_seq || 1).padStart(w, "0");
        setInvoicePrefix(prefix || "INV");
        setCustomInvoiceNo(seq);
        setNextInvoicePreview(prefix ? `${prefix}-${seq}` : seq);
      }).catch(() => {});
    }
  });
  useEffect(() => { load(); }, [id]);
  if (!order) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  const canInvoice = order.status === "pending";
  const canDispatch = order.status === "invoiced";
  const invSubtotal = order ? order.items.reduce((s, it) => {
    if (removedIds.has(it.product_id)) return s;
    const d = dispatch[it.product_id] || { boxes: 0, pcs: 0 };
    const b = Number(d.boxes || 0), p = Number(d.pcs || 0);
    return s + (it.box_price || 0) * b + (it.pcs_price || 0) * p;
  }, 0) + extraItems.reduce((s, it) => s + (it.box_price || 0) * it.boxes + (it.pcs_price || 0) * it.pcs, 0) : 0;
  const invEffectiveReceived = billType === "cash" ? invSubtotal : (receivedChecked ? Number(receivedAmount || 0) : 0);

  const doInvoice = async () => {
    if (!window.confirm("Generate Invoice for this order? An invoice number will be created.")) return;
    setBusy(true);
    try {
      for (const it of order.items) {
        if (removedIds.has(it.product_id)) continue;
        const al = manualAllocs(it);
        if (al) {
          const sum = al.reduce((s, x) => s + x.qty_pcs, 0);
          if (sum !== pcsTotal(it)) {
            toast.error(`${it.product_name}: batch split (${sum} pcs) must equal invoicing qty (${pcsTotal(it)} pcs)`);
            setBusy(false);
            return;
          }
        }
      }
      const items = order.items
        .filter(it => !removedIds.has(it.product_id))
        .map(it => {
          const row = { product_id: it.product_id, qty_boxes: Number(dispatch[it.product_id]?.boxes || 0), qty_pcs: Number(dispatch[it.product_id]?.pcs || 0) };
          const al = manualAllocs(it);
          if (al) row.allocations = al;
          return row;
        });
      const extra_items = extraItems.map(it => ({ product_id: it.product_id, qty_boxes: it.boxes, qty_pcs: it.pcs }));
      const composedInvoiceNo = (invoicePrefix ? `${invoicePrefix}-${customInvoiceNo}` : customInvoiceNo).trim();
      const r = await dms.generateInvoiceSecondary(id, {
        items,
        extra_items,
        invoice_no: composedInvoiceNo,
        billing_name: billingName,
        eway_bill_no: ewayInput,
        bill_type: billType,
        received_amount: invEffectiveReceived,
        payment_type: paymentType === "bank" ? "bank" : paymentType,
        bank_account_id: paymentType !== "cash" && paymentType !== "cheque" ? paymentType : "",
        reference_no: paymentType !== "cash" ? referenceNo : "",
        state_of_supply: stateOfSupply,
        description,
        notes: invNotes,
        transport: { mode: transportMode, vehicle_no: vehicleNo, transporter, lr_no: lrNo },
        attachment: invAttachment || undefined,
      });
      toast.success(`Invoice ${r.invoice_no} generated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const doCancel = async () => {
    if (order.status === "invoiced") {
      const ok = window.confirm(`Cancel invoice ${order.invoice_no}?\n\nThe order will revert to Pending so a new invoice can be generated.`);
      if (!ok) return;
      setBusy(true);
      try { await dms.deleteRetailerBill(order.bill_id); toast.success("Invoice cancelled"); load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel invoice"); }
      setBusy(false);
      return;
    }
    const reason = window.prompt("Cancel this order? Enter a reason (optional):", "");
    if (reason === null) return;
    setBusy(true);
    try { await dms.cancelSecondaryOrder(id, reason); toast.success("Order cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
    setBusy(false);
  };
  const openEditChallanNo = async () => {
    const no = order.challan_no || "";
    const idx = no.lastIndexOf("-");
    setCPrefix(idx === -1 ? "" : no.slice(0, idx));
    setCNumber(idx === -1 ? no : no.slice(idx + 1));
    setCAddingPrefix(false); setCNewPrefix("");
    try { const s = await dms.getNumberSettings(); setCKnownPrefixes(s.delivery_challan?.known_prefixes || []); } catch (e) { /* ignore */ }
    setEditChallanOpen(true);
  };
  const saveChallanNo = async () => {
    const num = cNumber.trim();
    if (!num) { toast.error("Number is required"); return; }
    const newNo = cPrefix ? `${cPrefix}-${num}` : num;
    setSavingC(true);
    try {
      await dms.updateChallanNumber(id, newNo);
      try { await dms.updateNumberSettings("delivery_challan", { prefix: cPrefix }); } catch (e) { /* non-fatal */ }
      toast.success("Challan number updated");
      setEditChallanOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setSavingC(false);
  };
  const doDispatch = async () => {
    if (!window.confirm("Dispatch this order? Stock will be deducted and a Delivery Challan generated.")) return;
    setBusy(true);
    try {
      const r = await dms.dispatchSecondary(id, {});
      toast.success(`Dispatched — Challan ${r.challan_no} generated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const saveEwayBill = async () => {
    setSavingEway(true);
    try { await dms.updateRetailerBillEwayBill(order.bill_id, { eway_bill_no: ewayInput.trim() }); toast.success("e-Way Bill number saved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSavingEway(false);
  };
  const openEditInvoiceNo = async () => {
    const no = order.invoice_no || "";
    const idx = no.lastIndexOf("-");
    setNoPrefix(idx === -1 ? "" : no.slice(0, idx));
    setNoNumber(idx === -1 ? no : no.slice(idx + 1));
    setNoAddingPrefix(false); setNoNewPrefix("");
    try { const s = await dms.getNumberSettings(); setNoKnownPrefixes(s.retailer_bill?.known_prefixes || []); } catch (e) { /* ignore */ }
    setEditNoOpen(true);
  };
  const saveInvoiceNo = async () => {
    const num = noNumber.trim();
    if (!num) { toast.error("Number is required"); return; }
    const newNo = noPrefix ? `${noPrefix}-${num}` : num;
    setSavingNo(true);
    try {
      await dms.updateRetailerBillNumber(order.bill_id, newNo);
      try { await dms.updateNumberSettings("retailer_bill", { prefix: noPrefix }); } catch (e) { /* non-fatal */ }
      toast.success("Invoice number updated");
      setEditNoOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setSavingNo(false);
  };
  return (
    <div>
      <PageHeader title={order.order_no} subtitle={`${order.retailer_name} • Placed ${niceDate(order.created_at)}`} back="/dms/distributor/retail-orders"
        action={<div className="flex gap-2 flex-wrap">
          {order.bill_id && <Button variant="outline" onClick={() => window.open(`/dms/print/retailer-bill/${order.bill_id}`, "_blank")}><Printer size={14} className="mr-1" /> Print Invoice</Button>}
          {order.challan_id && <Button variant="outline" onClick={() => window.open(`/dms/print/challan/${order.challan_id}`, "_blank")}><FileText size={14} className="mr-1" /> Print Challan</Button>}
          <OwnerEditOrder order={order} onDone={load} />
          {canInvoice && <Button onClick={doInvoice} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="invoice-btn"><Receipt size={14} className="mr-1" /> Generate Invoice</Button>}
          {canDispatch && <Button onClick={doDispatch} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="dispatch-btn"><Truck size={14} className="mr-1" /> Dispatch</Button>}
          {(order.status === "pending" || order.status === "invoiced") && <Button variant="outline" onClick={doCancel} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cancel-order-btn">{order.status === "invoiced" ? "Cancel Invoice" : "Cancel Order"}</Button>}
        </div>} />
      {/* Flow progress */}
      <div className="flex items-center gap-2 mb-4 text-xs font-medium">
        {["pending", "invoiced", "dispatched"].map((st, i) => {
          const order_seq = { pending: 0, invoiced: 1, dispatched: 2 };
          const cur = order_seq[order.status] ?? 0;
          const label = ["Order Placed", "Invoice Generated", "Dispatched + Challan"][i];
          const done = i <= cur;
          return (
            <React.Fragment key={st}>
              {i > 0 && <div className={`h-0.5 w-6 ${i <= cur ? "bg-emerald-500" : "bg-slate-200"}`} />}
              <span className={`px-2.5 py-1 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{label}</span>
            </React.Fragment>
          );
        })}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Ordered By</div><div className="mt-1 text-sm font-medium text-slate-900">{order.placed_by_role === "salesperson" ? (order.placed_by_name || "Salesperson") : (order.retailer_name || "Retailer")}</div>{order.placed_by_role === "salesperson" && <div className="text-[11px] text-slate-400 mt-0.5">for {order.retailer_name}</div>}{order.placed_by_role !== "salesperson" && <div className="text-[11px] text-slate-400 mt-0.5">self-placed</div>}</Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Status</div><div className="mt-1"><span className={`text-sm px-2.5 py-1 rounded-full border ${(order.is_balance_order && order.status === "pending") ? "bg-orange-100 text-orange-700 border-orange-300" : statusPill(order.status)}`}>{(order.is_balance_order && order.status === "pending") ? "balance due" : order.status.replace(/_/g, " ")}</span></div></Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Invoice No</div>
          <div className="mt-1 flex items-center justify-between">
            <span className={order.invoice_no ? "font-semibold" : "font-medium text-slate-400 italic"}>{order.invoice_no || (customInvoiceNo.trim() || nextInvoicePreview || "Auto-generate")}</span>
            <div className="flex items-center gap-1">
              {order.bill_id && order.status === "invoiced" && (
                <button
                  onClick={async () => {
                    const ok = window.confirm(`Delete invoice ${order.invoice_no}?\n\nThe order will revert to Pending so a new invoice can be generated.`);
                    if (!ok) return;
                    try { await dms.deleteRetailerBill(order.bill_id); toast.success("Invoice deleted"); load(); }
                    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
                  }}
                  className="p-1 hover:bg-red-50 rounded" title="Delete invoice"
                  data-testid="delete-invoice-btn"
                >
                  <Trash2 size={14} className="text-red-500" />
                </button>
              )}
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Challan No</div>
          <div className="mt-1 flex items-center justify-between">
            <span className="font-semibold">{order.challan_no || "—"}</span>
          </div>
          {order.challan_no_history && order.challan_no_history.length > 0 && (
            <div className="mt-1 text-[10px] text-slate-400">
              Previous: {order.challan_no_history.map(h => h.challan_no).join(", ")}
            </div>
          )}
        </Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Order Value</div><div className="mt-1 font-bold text-lg">{inr(order.total ?? order.subtotal)}</div></Card>
      </div>
      {order.bill_id && (
        <Card className="p-4 mb-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-600 shrink-0">e-Way Bill No.</span>
          <Input value={ewayInput} onChange={e => setEwayInput(e.target.value)} placeholder="Enter e-Way Bill no. from Vyapar" className="max-w-xs" disabled={savingEway} data-testid="eway-bill-input-retailer" />
          <Button size="sm" variant="outline" onClick={saveEwayBill} disabled={savingEway || ewayInput.trim() === (order.bill?.eway_bill_no || "")} data-testid="save-eway-bill-btn-retailer">Save</Button>
          {order.bill?.eway_bill_no && <span className="text-xs text-emerald-600">Saved {order.bill.eway_bill_date ? niceDate(order.bill.eway_bill_date) : ""}</span>}
        </Card>
      )}
      <Dialog open={editChallanOpen} onOpenChange={setEditChallanOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Challan No.</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Prefix</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setCPrefix("")} className={`px-3 py-1.5 rounded-full text-sm border ${!cPrefix ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>None</button>
                {cKnownPrefixes.map(p => (
                  <button key={p} type="button" onClick={() => setCPrefix(p)} className={`px-3 py-1.5 rounded-full text-sm border ${cPrefix === p ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>{p}</button>
                ))}
                {cAddingPrefix ? (
                  <div className="flex items-center gap-1">
                    <Input value={cNewPrefix} onChange={e => setCNewPrefix(e.target.value)} placeholder="e.g. DL" className="w-24 h-8 text-sm" autoFocus />
                    <Button size="sm" onClick={() => { const p = cNewPrefix.trim().toUpperCase(); if (p) { setCPrefix(p); setCKnownPrefixes(prev => prev.includes(p) ? prev : [...prev, p]); } setCNewPrefix(""); setCAddingPrefix(false); }}>Add</Button>
                  </div>
                ) : (
                  <button type="button" onClick={() => { setCAddingPrefix(true); setCNewPrefix(""); }} className="px-3 py-1.5 rounded-full text-sm border border-dashed border-slate-300 text-[#a67c00]">+ Add Prefix</button>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Number</div>
              <Input value={cNumber} onChange={e => setCNumber(e.target.value)} data-testid="challan-no-number-input" />
            </div>
          </div>
          <DialogFooter><Button onClick={saveChallanNo} disabled={savingC} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-challan-no-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={editNoOpen} onOpenChange={setEditNoOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Invoice No.</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Prefix</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setNoPrefix("")} className={`px-3 py-1.5 rounded-full text-sm border ${!noPrefix ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>None</button>
                {noKnownPrefixes.map(p => (
                  <button key={p} type="button" onClick={() => setNoPrefix(p)} className={`px-3 py-1.5 rounded-full text-sm border ${noPrefix === p ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>{p}</button>
                ))}
                {noAddingPrefix ? (
                  <div className="flex items-center gap-1">
                    <Input value={noNewPrefix} onChange={e => setNoNewPrefix(e.target.value)} placeholder="e.g. MAR" className="w-24 h-8 text-sm" autoFocus />
                    <Button size="sm" onClick={() => { const p = noNewPrefix.trim().toUpperCase(); if (p) { setNoPrefix(p); setNoKnownPrefixes(prev => prev.includes(p) ? prev : [...prev, p]); } setNoNewPrefix(""); setNoAddingPrefix(false); }}>Add</Button>
                  </div>
                ) : (
                  <button type="button" onClick={() => { setNoAddingPrefix(true); setNoNewPrefix(""); }} className="px-3 py-1.5 rounded-full text-sm border border-dashed border-slate-300 text-[#a67c00]">+ Add Prefix</button>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Number</div>
              <Input value={noNumber} onChange={e => setNoNumber(e.target.value)} data-testid="invoice-no-number-input" />
            </div>
          </div>
          <DialogFooter><Button onClick={saveInvoiceNo} disabled={savingNo} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-invoice-no-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {canInvoice && (
        <Card className="p-4 mb-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label>Invoice Prefix</Label>
              <Input value={invoicePrefix} onChange={e => setInvoicePrefix(e.target.value)} className="mt-1" data-testid="inv-prefix-input" />
            </div>
            <div>
              <Label>Invoice No.*</Label>
              <Input value={customInvoiceNo} onChange={e => setCustomInvoiceNo(e.target.value)} placeholder="Auto-generate" className="mt-1" data-testid="custom-invoice-no-input" />
            </div>
            <div>
              <Label>Retailer</Label>
              <div className="mt-1 h-10 flex items-center px-3 rounded-md border bg-slate-50 text-sm font-medium">{order.retailer_name || "—"}</div>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant={billType === "credit" ? "default" : "outline"} className={billType === "credit" ? "bg-emerald-600 hover:bg-emerald-700" : ""} onClick={() => setBillType("credit")}>Credit</Button>
              <Button type="button" variant={billType === "cash" ? "default" : "outline"} className={billType === "cash" ? "bg-emerald-600 hover:bg-emerald-700" : ""} onClick={() => setBillType("cash")}>Cash</Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Billing Name (optional)</Label>
              <Input value={billingName} onChange={e => setBillingName(e.target.value)} placeholder="Defaults to party name" className="mt-1" />
            </div>
            <div>
              <Label>E-Way Bill No.</Label>
              <Input value={ewayInput} onChange={e => setEwayInput(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>State of Supply</Label>
              <Input value={stateOfSupply} onChange={e => setStateOfSupply(e.target.value)} className="mt-1" />
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">Transportation Details (optional)</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input placeholder="Mode (Road / Rail)" value={transportMode} onChange={e => setTransportMode(e.target.value)} />
              <Input placeholder="Vehicle No." value={vehicleNo} onChange={e => setVehicleNo(e.target.value)} />
              <Input placeholder="Transporter" value={transporter} onChange={e => setTransporter(e.target.value)} />
              <Input placeholder="LR / Docket No." value={lrNo} onChange={e => setLrNo(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">Payment</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <Label>Payment Type</Label>
                <select className="mt-1 w-full h-10 rounded-md border px-3 text-sm" value={paymentType} onChange={e => setPaymentType(e.target.value)}>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              {billType === "credit" && (
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={receivedChecked} onChange={e => setReceivedChecked(e.target.checked)} id="inv-received-checkbox" />
                  <Label htmlFor="inv-received-checkbox">Received</Label>
                  <Input type="number" disabled={!receivedChecked} value={receivedAmount} onChange={e => setReceivedAmount(e.target.value)} className="w-28" />
                </div>
              )}
              {paymentType !== "cash" && (
                <div>
                  <Label>{paymentType === "cheque" ? "Cheque No." : "Transaction/Ref No."}</Label>
                  <Input value={referenceNo} onChange={e => setReferenceNo(e.target.value)} placeholder={paymentType === "cheque" ? "Cheque number" : "UTR / Ref number"} className="mt-1" />
                </div>
              )}
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Balance Due</div>
                <div className="text-lg font-bold text-amber-700">{inr(Math.max(0, Math.round((invSubtotal - invEffectiveReceived) * 100) / 100))}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Notes</Label>
              <Textarea value={invNotes} onChange={e => setInvNotes(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} className="mt-1" />
            </div>
          </div>

          <div>
            <Label>Add Document (optional)</Label>
            <input type="file" accept="image/*,.pdf" onChange={onPickInvDocument} className="block text-sm mt-1" />
            {invAttachment && <div className="text-xs text-slate-500 mt-1">Attached: {invAttachment.name} <button className="text-rose-600 ml-2" onClick={() => setInvAttachment(null)}>Remove</button></div>}
          </div>
        </Card>
      )}
      <Card>
        <div className="p-4 border-b border-slate-100 font-semibold flex items-center justify-between">
          <span>{canInvoice ? "Items — set invoice quantities" : "Items"}</span>
          {canInvoice && <Button size="sm" variant="outline" onClick={openAddItem} data-testid="add-item-btn"><Plus size={14} className="mr-1" /> Add Item</Button>}
        </div>
        <Table><TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Ordered</TableHead>{canInvoice && <TableHead>Your Stock</TableHead>}<TableHead>{canInvoice ? "Invoicing" : "Invoiced / Dispatched"}</TableHead><TableHead>Line Total</TableHead>{canInvoice && <TableHead></TableHead>}</TableRow></TableHeader><TableBody>
          {order.items.map(it => {
            if (canInvoice && removedIds.has(it.product_id)) return null;
            if (!canInvoice) {
              // In the read-only (post-invoice) view, hide any line that was
              // removed before invoicing — nothing was invoiced/dispatched for it.
              const shownQty = order.status === "dispatched" ? (it.qty_boxes_dispatched ?? 0) : (it.qty_boxes_invoiced ?? 0);
              const shownPcs = order.status === "dispatched" ? (it.qty_pcs_dispatched ?? 0) : (it.qty_pcs_invoiced ?? 0);
              if (shownQty <= 0 && shownPcs <= 0) return null;
            }
            const d = dispatch[it.product_id] || { boxes: 0, pcs: 0 };
            return (
              <TableRow key={it.product_id}>
                <TableCell><div className="font-medium">{it.product_name}</div>{it.carried_pending && <span className="text-[10px] uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Pending Carry-forward</span>}</TableCell>
                <TableCell>{Math.round((it.qty_boxes_ordered || 0) * (it.box_qty || 1) + (it.qty_pcs_ordered || 0))} pcs</TableCell>
                {canInvoice && (() => {
                  const sPcs = Number(it.stock_pcs ?? 0);
                  const iPcs = Number(d.boxes || 0) * (it.box_qty || 1) + Number(d.pcs || 0);
                  const over = iPcs > sPcs + 0.001;
                  return (
                    <TableCell>
                      <div className={`font-semibold ${over || sPcs <= 0 ? "text-rose-600" : "text-emerald-700"}`}>{Math.round(sPcs)} pcs</div>
                      <div className="text-[11px] text-slate-500">{Number(it.stock_boxes ?? 0)} bx</div>
                      {over && <div className="text-[10px] text-rose-600">Insufficient stock</div>}
                    </TableCell>
                  );
                })()}
                <TableCell>
                  {canInvoice ? (
                    <div>
                    <div className="flex items-center gap-2">
                      <Input type="number" step="0.1" min={0} max={it.qty_boxes_ordered} value={d.boxes} onChange={e => setDispatch({ ...dispatch, [it.product_id]: { ...d, boxes: e.target.value } })} className="w-20" />
                      <span className="text-xs text-slate-500">bx</span>
                      {order.mode === "box_pcs" && it.qty_pcs_ordered > 0 && <>
                        <Input type="number" min={0} max={it.qty_pcs_ordered} value={d.pcs} onChange={e => setDispatch({ ...dispatch, [it.product_id]: { ...d, pcs: e.target.value } })} className="w-20" />
                        <span className="text-xs text-slate-500">pcs</span>
                      </>}
                    </div>
                    {batchPicker(it)}
                    </div>
                  ) : (
                    <span className="font-semibold">
                      {Math.round((order.status === "dispatched" ? (it.qty_boxes_dispatched ?? 0) : (it.qty_boxes_invoiced ?? 0)) * (it.box_qty || 1) + (order.status === "dispatched" ? (it.qty_pcs_dispatched ?? 0) : (it.qty_pcs_invoiced ?? 0)))} pcs
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {canInvoice ? (() => {
                    const b = Number(d.boxes || 0);
                    const p = Number(d.pcs || 0);
                    const lineTotal = (it.box_price || 0) * b + (it.pcs_price || 0) * p;
                    return inr(Math.round(lineTotal * 100) / 100);
                  })() : inr(it.line_total)}
                </TableCell>
                {canInvoice && (
                  <TableCell>
                    <button onClick={() => setRemovedIds(prev => new Set(prev).add(it.product_id))} className="p-1 hover:bg-rose-50 rounded" title="Remove item" data-testid={`remove-item-${it.product_id}`}>
                      <Trash2 size={14} className="text-rose-500" />
                    </button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {canInvoice && extraItems.map(it => (
            <TableRow key={it.product_id} className="bg-emerald-50/40">
              <TableCell><div className="font-medium">{it.product_name}</div><span className="text-[10px] uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">New Item</span></TableCell>
              <TableCell className="text-slate-400 text-xs">Not ordered</TableCell>
              <TableCell className="text-slate-400 text-xs">—</TableCell>
              <TableCell>{it.boxes} boxes {it.pcs > 0 && `+ ${it.pcs} pcs`}</TableCell>
              <TableCell className="font-medium">{inr(Math.round(((it.box_price || 0) * it.boxes + (it.pcs_price || 0) * it.pcs) * 100) / 100)}</TableCell>
              <TableCell>
                <button onClick={() => removeExtraItem(it.product_id)} className="p-1 hover:bg-rose-50 rounded" title="Remove item">
                  <Trash2 size={14} className="text-rose-500" />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </Card>

      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Item to Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Product</Label>
              <Select value={addItemPid} onValueChange={setAddItemPid}>
                <SelectTrigger className="mt-1" data-testid="add-item-product-select"><SelectValue placeholder="Select a product" /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1.5 sticky top-0 bg-white z-10">
                    <Input
                      placeholder="Search product name or SKU…"
                      value={addItemSearch}
                      onChange={e => setAddItemSearch(e.target.value)}
                      onKeyDown={e => e.stopPropagation()}
                      className="h-8"
                      data-testid="add-item-product-search"
                      autoFocus
                    />
                  </div>
                  {addItemProducts
                    .filter(p => {
                      const needle = addItemSearch.trim().toLowerCase();
                      if (!needle) return true;
                      const name = (p.name || p.product_name || "").toLowerCase();
                      const sku = (p.sku_code || "").toLowerCase();
                      return name.includes(needle) || sku.includes(needle);
                    })
                    .map(p => (
                      <SelectItem key={p.id || p.product_id} value={p.id || p.product_id}>{p.name || p.product_name} ({p.sku_code})</SelectItem>
                    ))}
                  {addItemProducts.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No more products to add</div>}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Boxes</Label><Input type="number" step="0.1" min={0} value={addItemBoxes} onChange={e => setAddItemBoxes(e.target.value)} className="mt-1" data-testid="add-item-boxes-input" /></div>
              {order.mode === "box_pcs" && <div><Label>Pcs</Label><Input type="number" min={0} value={addItemPcs} onChange={e => setAddItemPcs(e.target.value)} className="mt-1" /></div>}
            </div>
          </div>
          <DialogFooter><Button onClick={confirmAddItem} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="confirm-add-item-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Secondary Ledger (used by distributor + distributor_accountant)
export function SecondaryLedgerPage() {
  const [data, setData] = useState({ entries: [], summary: [], period_totals: { billed: 0, paid: 0, due: 0 } });
  const [open, setOpen] = useState(false);
  const [retDetail, setRetDetail] = useState(null);
  const [form, setForm] = useState({ retailer_id: "", amount: "", method: "cash" });
  const [alloc, setAlloc] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [searchParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("date_to") || "");
  const [salespersonId, setSalespersonId] = useState("");

  const load = () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (salespersonId) params.salesperson_id = salespersonId;
    return Promise.all([dms.secondaryLedger(params), dms.listRetailers({ lite: 1 })])
      .then(([l, r]) => { setData(l); setRetailers(r.data); });
  };
  useEffect(() => { load(); }, [dateFrom, dateTo, salespersonId]);
  useEffect(() => { if (!open) setAlloc([]); }, [open]);
  useEffect(() => { dms.distSalespersons("me").then(d => setSalespersons(d.data || [])).catch(() => setSalespersons([])); }, []);
  const save = async () => {
    try { await dms.recordSecondaryPayment({ ...form, amount: Number(form.amount), allocations: alloc.length ? alloc : undefined }); toast.success("Payment recorded"); setOpen(false); setAlloc([]); setForm({ retailer_id: "", amount: "", method: "cash" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <LedgerEntryDialog entry={retDetail} onClose={() => setRetDetail(null)} />
      <PageHeader title="Secondary Sales Ledger" subtitle="Distributor ↔ Retailer transactions"
        action={<Button onClick={() => setOpen(true)} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="record-sec-payment"><IndianRupee size={16} className="mr-1" /> Record Payment</Button>} />

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="mt-1" data-testid="sec-ledger-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="mt-1" data-testid="sec-ledger-date-to" />
        </div>
        <div>
          <Label className="text-xs">Salesperson</Label>
          <select value={salespersonId} onChange={e => setSalespersonId(e.target.value)}
                  className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm"
                  data-testid="sec-ledger-salesperson-filter">
            <option value="">All Salespersons</option>
            {salespersons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {(dateFrom || dateTo || salespersonId) && (
          <Button variant="outline" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setSalespersonId(""); }}>Clear Filters</Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 bg-slate-50">
          <div className="text-xs uppercase tracking-wider text-slate-500">Billed {(dateFrom || dateTo) ? "(in range)" : ""}</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{inr(data.period_totals?.billed || 0)}</div>
        </Card>
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="text-xs uppercase tracking-wider text-amber-700">Returns {(dateFrom || dateTo) ? "(in range)" : ""}</div>
          <div className="text-xl font-bold text-amber-900 mt-1">{inr(data.period_totals?.returns || 0)}</div>
        </Card>
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <div className="text-xs uppercase tracking-wider text-emerald-700">Paid {(dateFrom || dateTo) ? "(in range)" : ""}</div>
          <div className="text-xl font-bold text-emerald-900 mt-1">{inr(data.period_totals?.paid || 0)}</div>
        </Card>
        <Card className="p-4 bg-rose-50 border-rose-200">
          <div className="text-xs uppercase tracking-wider text-rose-700">Due {(dateFrom || dateTo) ? "(in range)" : ""}</div>
          <div className="text-xl font-bold text-rose-900 mt-1">{inr(data.period_totals?.due || 0)}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {data.summary.map(s => (
          <Card key={s.retailer_id} className="p-4">
            <div className="text-sm font-semibold">{s.retailer_name}</div>
            <div className="mt-2 grid grid-cols-4 gap-2 text-center">
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Billed</div><div className="font-semibold text-sm">{inr(s.billed)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Returns</div><div className="font-semibold text-amber-700 text-sm">{inr(s.returns || 0)}</div></div><div><div className="text-[10px] uppercase tracking-wider text-slate-500">Paid</div><div className="font-semibold text-emerald-700 text-sm">{inr(s.paid)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Due</div><div className="font-bold text-rose-700 text-sm">{inr(s.outstanding)}</div></div>
            </div>
            <button onClick={() => { setForm({ retailer_id: s.retailer_id, amount: "", method: "cash" }); setOpen(true); }} className="mt-3 w-full text-xs text-[#a67c00] hover:bg-[#faf6e6] py-1.5 rounded">Mark payment</button>
          </Card>
        ))}
      </div>
      <Card>
        <div className="p-4 border-b border-slate-100 font-semibold">Entries</div>
        <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Retailer</TableHead><TableHead>Reference</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="w-12">Proof</TableHead></TableRow></TableHeader><TableBody>
          {data.entries.map(e => {
            const rn = retailers.find(r => r.id === e.retailer_id)?.name || e.retailer_id;
            return (
              <TableRow key={e.id} onClick={() => setRetDetail(e)} className="cursor-pointer hover:bg-slate-50"><TableCell className="text-xs text-slate-500">{niceDate(e.at)}</TableCell><TableCell className="font-medium">{rn}</TableCell><TableCell className="font-mono text-xs">{e.reference_no}</TableCell><TableCell><span className={`text-xs px-2 py-0.5 rounded ${e.kind === "invoice" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{e.kind}</span></TableCell><TableCell className={`text-right font-semibold ${e.kind === "invoice" ? "text-rose-700" : "text-emerald-700"}`}>{e.kind === "invoice" ? "+" : "-"}{inr(e.amount)}</TableCell><TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}><ProofButton proofId={e.proof_id} /></TableCell></TableRow>
            );
          })}
        </TableBody></Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent>
        <DialogHeader><DialogTitle>Record Payment from Retailer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Retailer *</Label>
            <Select value={form.retailer_id} onValueChange={v => setForm({ ...form, retailer_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{retailers.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border border-dashed border-slate-300 px-3 py-2">
            <span className="text-xs text-slate-500">Against which invoice?</span>
            <LinkInvoiceButton retailerId={form.retailer_id} amount={form.amount} setAmount={(v) => setForm(f => ({ ...f, amount: String(v) }))} value={alloc} onChange={setAlloc} />
          </div>
          <div><Label>Amount (₹) *</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="sec-pay-amt" /></div>
          <div><Label>Method</Label>
            <Select value={form.method} onValueChange={v => setForm({ ...form, method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-sec-payment">Save</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}


// Distributor edits own retailer's details (name locked; owner sees changes in Activity Log)
function RetailerProfileDialog({ r, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open || !r) return;
    setForm({
      phone: r.phone || "", address: r.address || "",
      gstin: r.kyc?.gstin || "", shop_license: r.kyc?.shop_license || "",
      bank_name: r.bank?.bank_name || "", bank_account: r.bank?.bank_account || "",
      bank_ifsc: r.bank?.bank_ifsc || "", upi_id: r.bank?.upi_id || "",
      gps_lat: r.gps_lat ?? "", gps_lng: r.gps_lng ?? "", location_link: r.location_link || "",
      documents: r.documents || [],
    });
  }, [open, r]);
  const set = (k, fn) => (e) => setForm({ ...form, [k]: fn ? fn(e.target.value) : e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const res = await dms.updateDistRetailerProfile(r.id, {
        ...form,
        gps_lat: form.gps_lat === "" ? null : Number(form.gps_lat),
        gps_lng: form.gps_lng === "" ? null : Number(form.gps_lng),
      });
      toast.success(res.changed ? "Retailer updated" : "Nothing changed");
      onClose();
      if (onSaved) onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Retailer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Business Name</Label>
            <Input value={r?.name || ""} disabled className="bg-slate-100 cursor-not-allowed" data-testid="dist-ret-name-locked" />
            <div className="text-[11px] text-slate-500 mt-1">Name cannot be changed</div>
          </div>
          <div><Label>Phone</Label><Input value={form.phone || ""} onChange={set("phone")} data-testid="dist-ret-phone" /></div>
          <div><Label>GSTIN</Label><Input value={form.gstin || ""} maxLength={15} onChange={set("gstin", v => v.toUpperCase())} data-testid="dist-ret-gstin" /></div>
          <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={set("address")} /></div>
          <div><Label>Shop / Trade License</Label><Input value={form.shop_license || ""} onChange={set("shop_license")} /></div>
          <div><Label>Bank Name</Label><Input value={form.bank_name || ""} onChange={set("bank_name")} /></div>
          <div><Label>Bank Account</Label><Input value={form.bank_account || ""} onChange={set("bank_account")} /></div>
          <div><Label>IFSC</Label><Input value={form.bank_ifsc || ""} onChange={set("bank_ifsc", v => v.toUpperCase())} /></div>
          <div><Label>UPI ID</Label><Input value={form.upi_id || ""} onChange={set("upi_id")} placeholder="name@bank" /></div>
          <div className="col-span-2">
            <LocationDocumentsBlock
              lat={form.gps_lat ?? ""} lng={form.gps_lng ?? ""} locationLink={form.location_link || ""}
              onLat={(v) => setForm(f => ({ ...f, gps_lat: v }))}
              onLng={(v) => setForm(f => ({ ...f, gps_lng: v }))}
              onLocationLink={(v) => setForm(f => ({ ...f, location_link: v }))}
              documents={form.documents || []}
              onDocuments={(docs) => setForm(f => ({ ...f, documents: docs }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="dist-ret-save">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
